/**
 * ============================================================
 * KEJJA OS — src/pages/settings/SettingsLayout.tsx
 * Sky accent. 4 tabs: Profile, Home, Preferences, Members.
 * ============================================================
 */

import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import { Settings as SettingsIcon, User, Home as HomeIcon, Sliders, Users } from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface SettingsContext {
  homeId: string;
  currentUserId: string | undefined;
  homeName: string;
  myRole: string | null;
}

export function useSettingsContext(): SettingsContext {
  return useOutletContext<SettingsContext>();
}

const TABS = [
  { to: '/settings',             label: 'Profile',     icon: User,    end: true },
  { to: '/settings/home',        label: 'Home',        icon: HomeIcon },
  { to: '/settings/preferences', label: 'Preferences', icon: Sliders }, { to: '/settings/theme', label: 'Theme', icon: 'palette' },
  { to: '/settings/members',     label: 'Members',     icon: Users },
];

export default function SettingsLayout() {
  const { data: homesData, isLoading } = useMyHomes();
  const first = homesData?.homes?.[0];
  const homeId = first?.home.id;
  const homeName = first?.home.name ?? '';
  const myRole = first?.role ?? null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (!homeId) return <div className="text-sm text-muted-foreground">No home found.</div>;

  const ctx: SettingsContext = {
    homeId, currentUserId: undefined, homeName, myRole,
  };

  return (
    <div className="space-y-5">
      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 flex items-center justify-center flex-shrink-0">
            <SettingsIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold leading-tight tracking-tight">Settings</h1>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              <span className="italic">Make Kejja yours.</span>
              {homeName && <span className="ml-2">· {homeName}</span>}
            </p>
          </div>
        </div>
      </header>

      <nav className="flex items-center gap-1 overflow-x-auto">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to} to={tab.to} end={tab.end}
            className={({ isActive }) => cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={ctx} />
    </div>
  );
}
